import type { CarrierDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { AlertTriangle, Pencil, Tag, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useShopFlag } from "@/hooks/use-flag";
import { extractCarrierTagValue } from "../lib/tag-conditions.helpers";
import { isMissingTrackingCredentials } from "../lib/carrier-credentials.helpers";
import { ChronopostELabelBadge } from "./chronopost-elabel-badge";
import SimpleMenu from "./simple-menu";
import { TagConditionModal } from "./tag-condition-modal";

interface CarrierListItemProps {
  carrier: CarrierDto;
  shopId: string;
  onEdit: (carrierId: string) => void;
  onDelete: (carrierId: string) => void;
}

export const CarrierListItem = ({
  carrier,
  shopId,
  onEdit,
  onDelete,
}: CarrierListItemProps) => {
  const t = useTranslation();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const currentTag = useMemo(
    () => extractCarrierTagValue(carrier.conditionalRules),
    [carrier.conditionalRules],
  );
  const isCarrierTagConditionEnabled = useShopFlag(
    "isCarrierTagConditionEnabled",
  );
  const missingCredentials = isMissingTrackingCredentials(carrier);

  return (
    <>
      <div className="flex justify-between group">
        <div className="flex space-x-8 text-sm items-center">
          <div className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-green-400 rounded-full"></div>
            {carrier.code.toUpperCase()}
          </div>
          {carrier.code === "fedex" && (
            <img
              src="/fedex.png"
              alt="FedEx"
              className="h-5 w-5 object-contain"
            />
          )}
          <div>
            {carrier.preferences.webSiteId ||
              carrier.preferences.accountId ||
              carrier.preferences.accountNumber ||
              carrier.preferences.subAccount}
          </div>
          <ChronopostELabelBadge carrier={carrier} />
          {missingCredentials && (
            <span
              title={t("carrier.missingCredentials.hint")}
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-200"
            >
              <AlertTriangle className="h-3 w-3" />
              {t("carrier.missingCredentials.badge")}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {/* Display current tag if exists */}
          {isCarrierTagConditionEnabled && currentTag && (
            <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">
              <Tag className="h-3 w-3 mr-1" />
              {currentTag}
            </div>
          )}

          <SimpleMenu
            iconTitle={<DotsHorizontalIcon className="h-4 w-4" />}
            type={"neutral"}
            menu={[
              {
                onClick: () => onEdit(carrier.id),
                title: t("settings.integrations.webhooks.menu.edit"),
                icon: <Pencil className="h-4 w-4 mr-1" />,
              },
              ...(isCarrierTagConditionEnabled
                ? [
                    {
                      onClick: () => setIsTagModalOpen(true),
                      title: currentTag
                        ? t("carrier.tagCondition.editMenu")
                        : t("carrier.tagCondition.addMenu"),
                      icon: <Tag className="h-4 w-4 mr-1" />,
                    },
                  ]
                : []),
              {
                onClick: () => onDelete(carrier.id),
                title: "",
                renderTitle: () => (
                  <p className="text-red-500">
                    {t("settings.integrations.webhooks.menu.delete")}
                  </p>
                ),
                icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
              },
            ]}
          />
        </div>
      </div>

      {isCarrierTagConditionEnabled && (
        <TagConditionModal
          carrier={carrier}
          shopId={shopId}
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
        />
      )}
    </>
  );
};
